/**
 * HMVMReviewer Theme Context
 * 
 * React context provider for managing theme state across the application.
 * Provides theme values and setters to all components.
 */

import { createContext, useContext, useCallback, useEffect, useState, ReactNode } from "react";
import {
  type ReviewerThemeBase,
  type ReviewerThemeBaseResolved,
  type ReviewerThemeAccent,
  readStoredThemeBase,
  readStoredThemeAccent,
  resolveThemeBase,
  setThemeBase,
  setThemeAccent,
  watchThemeSystem,
} from "../lib/reviewerTheme";

interface ThemeContextValue {
  // Base theme (dark/light/system)
  themeBase: ReviewerThemeBase;
  themeBaseResolved: ReviewerThemeBaseResolved;
  setThemeBase: (base: ReviewerThemeBase) => void;
  
  // Accent theme (electric-blue/neutral/etc)
  themeAccent: ReviewerThemeAccent;
  setThemeAccent: (accent: ReviewerThemeAccent) => void;
  
  // Helpers
  isDark: boolean;
  isLight: boolean;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const [themeBase, setThemeBaseState] = useState<ReviewerThemeBase>(() => readStoredThemeBase());
  const [themeBaseResolved, setThemeBaseResolved] = useState<ReviewerThemeBaseResolved>(() =>
    resolveThemeBase(readStoredThemeBase())
  );
  const [themeAccent, setThemeAccentState] = useState<ReviewerThemeAccent>(() => readStoredThemeAccent());

  // Watch for system theme changes
  useEffect(() => {
    return watchThemeSystem();
  }, []);

  // Listen for localStorage changes from other tabs
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "reviewer:theme-base") {
        const nextBase = readStoredThemeBase();
        setThemeBaseState(nextBase);
        setThemeBaseResolved(resolveThemeBase(nextBase));
      } else if (e.key === "reviewer:theme-accent") {
        const nextAccent = readStoredThemeAccent();
        setThemeAccentState(nextAccent);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const handleSetThemeBase = useCallback((base: ReviewerThemeBase) => {
    const resolved = setThemeBase(base);
    setThemeBaseState(base);
    setThemeBaseResolved(resolved);
  }, []);

  const handleSetThemeAccent = useCallback((accent: ReviewerThemeAccent) => {
    setThemeAccent(accent);
    setThemeAccentState(accent);
  }, []);

  const value: ThemeContextValue = {
    themeBase,
    themeBaseResolved,
    setThemeBase: handleSetThemeBase,
    themeAccent,
    setThemeAccent: handleSetThemeAccent,
    isDark: themeBaseResolved === "dark",
    isLight: themeBaseResolved === "light",
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

/**
 * Hook to access theme context
 */
export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}
